import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
  useMsal,
} from "@azure/msal-react";
import styled from "styled-components";
import Footer from "./Footer";
import Header from "./Header";
import Sidebar from "./Sidebar";
import AICopilotPanel from "./AICopilotPanel";
import InviteSignupNotice from "./InviteSignupNotice";
import { useEffect, useRef, useState } from "react";
import { loginRequest } from "../config/msalConfig";
import { InteractionStatus } from "@azure/msal-browser";
import { App } from "antd";
import { useLocation } from "react-router-dom";
import Lottie from "lottie-react";
import { motion } from "framer-motion";
import { useUser } from "components/UserContext";
import AI_FEATURE_KEYS from "constants/aiFeatureKeys";
import insightsAIAnimation from "../assets/Ai-powered-marketing-tools-abstract.logo-laptop.v20-final.json";

const AIInsightsIcon = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 64 64" aria-hidden="true">
    <rect x="6" y="6" width="34" height="22" rx="8" fill="#2b3a8a" />
    <polygon points="14,28 18,28 12,34" fill="#2b3a8a" />
    <circle cx="18" cy="17" r="3" fill="#ffffff" />
    <circle cx="30" cy="17" r="3" fill="#ffffff" />
    <rect x="24" y="24" width="34" height="22" rx="8" fill="#f88109" />
    <polygon points="46,46 50,46 54,52" fill="#f88109" />
  </svg>
);

export const PageLayout = (props) => {
  const { hasFeatureAccess } = useUser();
  const location = useLocation();
  const [fabOpen, setFabOpen] = useState(false);
  const [fabPosition, setFabPosition] = useState(null);
  const [fabDragging, setFabDragging] = useState(false);
  const [fabUserMoved, setFabUserMoved] = useState(false);
  const [fabDocked, setFabDocked] = useState(false);
  const [fabLastPosition, setFabLastPosition] = useState(null);
  const [fabLastWasDefault, setFabLastWasDefault] = useState(true);
  const [showDockButton, setShowDockButton] = useState(false);
  const [hideFabForCopilot, setHideFabForCopilot] = useState(false);
  const [hideFabForReport, setHideFabForReport] = useState(false);
  const [hideFabForModuleSummary, setHideFabForModuleSummary] = useState(false);
  const FAB_HITBOX = 90;
  const FAB_VISUAL = 120;
  const FAB_SCALE = 0.9;
  const FAB_MARGIN_X = 24;
  const FAB_MARGIN_TOP = 8;
  const FAB_FOOTER_GAP = 12;
  const FAB_EXTRA_OFFSET = Math.max(0, (FAB_VISUAL - FAB_HITBOX) / 2);
  const isReportRoute = location.pathname.startsWith("/report/");
  const hideCopilotMenu = !isReportRoute;
  const canModuleInsights = hasFeatureAccess(AI_FEATURE_KEYS.MODULE_LEVEL_OVERVIEW);
  const canReportInsights = hasFeatureAccess(AI_FEATURE_KEYS.PAGE_LEVEL_OVERVIEW);
  const canAssistant = hasFeatureAccess(AI_FEATURE_KEYS.AI_ASSISTANT_ACCESS);
  const hasAnyAIActions =
    (!hideCopilotMenu && canModuleInsights) ||
    (isReportRoute && canReportInsights) ||
    canAssistant;
  const hideFab = hideFabForCopilot || hideFabForReport || hideFabForModuleSummary || fabDocked || !hasAnyAIActions;
  const fabRef = useRef(null);
  const fabButtonRef = useRef(null);
  const footerRef = useRef(null);
  const [footerHeight, setFooterHeight] = useState(0);
  const fabDragRef = useRef({
    dragging: false,
    moved: false,
    startX: 0,
    startY: 0,
    offsetX: 0,
    offsetY: 0,
  });
  const ignoreClickRef = useRef(false);

  // Track sidebar collapse state for responsive layout
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const saved = localStorage.getItem("sidebarCollapsed");
    return saved === "true" ? 80 : 270;
  });

  useEffect(() => {
    // Listen for sidebar toggle events
    const handleSidebarToggle = (event) => {
      const { isCollapsed } = event.detail;
      setSidebarWidth(isCollapsed ? 80 : 270);
    };

    window.addEventListener("sidebarToggle", handleSidebarToggle);
    return () => window.removeEventListener("sidebarToggle", handleSidebarToggle);
  }, []);

  // const { instance, inProgress } = useMsal();
  // let activeAccount;

  // if (instance) {
  //     activeAccount = instance.getActiveAccount();
  // }

  // useEffect(() => {
  //   if(!activeAccount && inProgress === InteractionStatus.None){
  //     instance.loginRedirect(loginRequest).catch((error) => console.log(error));
  //   }
  // }, [inProgress, instance, activeAccount]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!fabRef.current) return;
      if (!fabRef.current.contains(event.target)) {
        setFabOpen(false);
      }
    };
    const handleWindowBlur = () => {
      setFabOpen(false);
    };

    if (fabOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      window.addEventListener("blur", handleWindowBlur);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("blur", handleWindowBlur);
    };
  }, [fabOpen]);

  useEffect(() => {
    const handleCopilotVisibility = (event) => {
      const isOpen = Boolean(event?.detail?.open);
      setHideFabForCopilot(isOpen);
      if (isOpen) {
        setFabOpen(false);
      }
    };
    const handleReportVisibility = (event) => {
      const isOpen = Boolean(event?.detail?.open);
      setHideFabForReport(isOpen);
      if (isOpen) {
        setFabOpen(false);
      }
    };
    const handleModuleSummaryVisibility = (event) => {
      const isOpen = Boolean(event?.detail?.open);
      setHideFabForModuleSummary(isOpen);
      if (isOpen) {
        setFabOpen(false);
      }
    };
    window.addEventListener("bic:ai-copilot-visibility", handleCopilotVisibility);
    window.addEventListener("bic:report-summary-visibility", handleReportVisibility);
    window.addEventListener("bic:module-summary-visibility", handleModuleSummaryVisibility);
    return () => {
      window.removeEventListener("bic:ai-copilot-visibility", handleCopilotVisibility);
      window.removeEventListener("bic:report-summary-visibility", handleReportVisibility);
      window.removeEventListener("bic:module-summary-visibility", handleModuleSummaryVisibility);
    };
  }, []);

  useEffect(() => {
    const updateFooterHeight = () => {
      setFooterHeight(footerRef.current?.offsetHeight || 0);
    };
    updateFooterHeight();

    if (footerRef.current && window.ResizeObserver) {
      const observer = new ResizeObserver(updateFooterHeight);
      observer.observe(footerRef.current);
      return () => observer.disconnect();
    }

    window.addEventListener("resize", updateFooterHeight);
    return () => window.removeEventListener("resize", updateFooterHeight);
  }, []);

  const getMaxFabY = () => {
    const footerOffset = footerHeight + FAB_FOOTER_GAP + FAB_EXTRA_OFFSET;
    const maxY = window.innerHeight - footerOffset - FAB_HITBOX;
    return Math.max(FAB_MARGIN_TOP, maxY);
  };

  const clampFabPosition = (x, y) => {
    const maxX = window.innerWidth - FAB_HITBOX - FAB_MARGIN_X;
    const maxY = getMaxFabY();
    return {
      x: Math.min(Math.max(x, FAB_MARGIN_X), maxX),
      y: Math.min(Math.max(y, FAB_MARGIN_TOP), maxY),
    };
  };

  const getDefaultPositionValue = () => {
    const initialX = window.innerWidth - 40 - FAB_HITBOX;
    const initialY = getMaxFabY();
    return clampFabPosition(initialX, initialY);
  };

  useEffect(() => {
    if (fabUserMoved) return;
    setFabPosition(null);
  }, [fabUserMoved, footerHeight, location.pathname]);

  useEffect(() => {
    const handleResize = () => {
      if (fabUserMoved) {
        setFabPosition((pos) => (pos ? clampFabPosition(pos.x, pos.y) : pos));
      } else {
        setFabPosition(null);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [fabUserMoved]);

  useEffect(() => {
    setFabUserMoved(false);
    setFabPosition(null);
  }, [location.pathname]);

  const handleFabPointerDown = (event) => {
    if (!fabRef.current) return;
    const rect = fabRef.current.getBoundingClientRect();
    fabDragRef.current = {
      dragging: true,
      moved: false,
      startX: event.clientX,
      startY: event.clientY,
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top,
    };
    setFabDragging(true);
    window.addEventListener("pointermove", handleFabPointerMove);
    window.addEventListener("pointerup", handleFabPointerUp);
  };

  const handleFabPointerMove = (event) => {
    if (!fabDragRef.current.dragging) return;
    const dx = event.clientX - fabDragRef.current.startX;
    const dy = event.clientY - fabDragRef.current.startY;
    if (!fabDragRef.current.moved && Math.hypot(dx, dy) > 3) {
      fabDragRef.current.moved = true;
    }
    const nextX = event.clientX - fabDragRef.current.offsetX;
    const nextY = event.clientY - fabDragRef.current.offsetY;
    setFabPosition(clampFabPosition(nextX, nextY));
  };

  const handleFabPointerUp = () => {
    if (fabDragRef.current.moved) {
      ignoreClickRef.current = true;
      setFabUserMoved(true);
    }
    fabDragRef.current.dragging = false;
    setFabDragging(false);
    window.removeEventListener("pointermove", handleFabPointerMove);
    window.removeEventListener("pointerup", handleFabPointerUp);
  };

  const handleFabClick = () => {
    if (ignoreClickRef.current) {
      ignoreClickRef.current = false;
      return;
    }
    setFabOpen((open) => !open);
  };

  const handleDockFab = (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (!fabUserMoved && !fabPosition) {
      setFabLastWasDefault(true);
      setFabLastPosition(null);
    } else {
      const rect = fabButtonRef.current?.getBoundingClientRect?.();
      const currentPosition = fabPosition
        ? clampFabPosition(fabPosition.x, fabPosition.y)
        : rect
          ? clampFabPosition(rect.left, rect.top)
          : getDefaultPositionValue();
      setFabLastWasDefault(false);
      setFabLastPosition(currentPosition);
    }
    setFabDocked(true);
    setFabOpen(false);
    setShowDockButton(false);
  };

  const handleUndockFab = () => {
    setFabDocked(false);
    setFabOpen(false);
    if (fabLastWasDefault || !fabLastPosition) {
      setFabUserMoved(false);
      setFabPosition(null);
      return;
    }
    if (fabLastPosition) {
      const clamped = clampFabPosition(fabLastPosition.x, fabLastPosition.y);
      setFabPosition(clamped);
      setFabUserMoved(true);
      return;
    }
    setFabUserMoved(false);
    setFabPosition(null);
  };

  const dockedFabButton = fabDocked ? (
    <button
      type="button"
      onClick={handleUndockFab}
      title="Insights AI"
      aria-label="Insights AI"
      style={{
        border: "none",
        background: "transparent",
        padding: 0,
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: 60,
        height: 60,
      }}
    >
      <Lottie
        animationData={insightsAIAnimation}
        loop={true}
        style={{
          width: 60,
          height: 60,
          pointerEvents: "none",
        }}
      />
    </button>
  ) : null;

  return (
    <>
      <AuthenticatedTemplate>
        <div className="flex flex-col min-h-screen gap-0 overflow-x-hidden">
          <App>
            <InviteSignupNotice />
            <Header />
            <div className="flex flex-row w-full mt-[77px] flex-grow h-[calc(100vh-77px)] overflow-hidden">
              <Sidebar />
              <div className="flex flex-col flex-grow min-h-0 overflow-x-hidden">
                {/* Main content */}
                <motion.div
                  initial={false}
                  animate={{ marginLeft: sidebarWidth + 30 }}
                  transition={{ duration: 0.3, ease: "easeInOut" }}
                  className="flex-grow overflow-y-auto overflow-x-hidden min-h-0"
                >
                  {props.children}
                </motion.div>

                {/* Footer */}
                <div
                  className="mt-auto"
                  style={{ marginLeft: sidebarWidth + 30 }}
                  ref={footerRef}
                >
                  <Footer dockSlot={dockedFabButton} />
                </div>
              </div>
            </div>
            <div
              ref={fabRef}
              style={{
                position: "fixed",
                left: fabPosition ? fabPosition.x : undefined,
                top: fabPosition ? fabPosition.y : undefined,
                right: fabPosition ? undefined : FAB_MARGIN_X,
                bottom: fabPosition
                  ? undefined
                  : footerHeight + FAB_FOOTER_GAP + FAB_EXTRA_OFFSET,
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-end",
                gap: 16,
                zIndex: 9999,
                visibility: hideFab ? "hidden" : "visible",
                pointerEvents: hideFab ? "none" : "auto",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 4,
                  marginBottom: -25,
                  opacity: fabOpen ? 1 : 0,
                  transform: fabOpen ? "translateY(0)" : "translateY(16px)",
                  pointerEvents: fabOpen ? "auto" : "none",
                  transition: "opacity 220ms ease, transform 220ms ease",
                }}
              >
                  {!hideCopilotMenu && canModuleInsights && (
                    <button
                      type="button"
                      onClick={() => {
                        setFabOpen(false);
                        window.dispatchEvent(
                          new CustomEvent("bic:open-ai-copilot", {
                            detail: { tab: "insights" },
                          })
                        );
                      }}
                      style={{
                        height: 40,
                        padding: "0 12px",
                        borderRadius: 999,
                        border: "1px solid #e0e5ea",
                        background: "rgba(248,129,9,0.08)",
                        boxShadow: "0 6px 16px rgba(0,0,0,0.12)",
                        cursor: "pointer",
                        fontSize: 12,
                        fontWeight: 600,
                        color: "#262626",
                        opacity: fabOpen ? 1 : 0,
                        transform: fabOpen ? "translateY(0)" : "translateY(8px)",
                        transition: "opacity 220ms ease, transform 220ms ease, background 160ms ease, color 160ms ease",
                        transitionDelay: "100ms",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = "#f88109";
                        e.currentTarget.style.color = "#ffffff";
                        e.currentTarget.style.transform = "translateY(0) scale(1.03)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = "rgba(248,129,9,0.08)";
                        e.currentTarget.style.color = "#262626";
                        e.currentTarget.style.transform = fabOpen ? "translateY(0)" : "translateY(8px)";
                      }}
                    >
                    Module Insights
                    </button>
                  )}
                  {isReportRoute && canReportInsights && (
                    <button
                      type="button"
                      onClick={() => {
                        setFabOpen(false);
                        window.dispatchEvent(
                          new CustomEvent("bic:open-ai-report-summary")
                        );
                      }}
                      style={{
                        height: 40,
                        padding: "0 12px",
                        borderRadius: 999,
                        border: "1px solid #e0e5ea",
                        background: "rgba(248,129,9,0.08)",
                        boxShadow: "0 6px 16px rgba(0,0,0,0.12)",
                        cursor: "pointer",
                        fontSize: 12,
                        fontWeight: 600,
                        color: "#262626",
                        opacity: fabOpen ? 1 : 0,
                        transform: fabOpen ? "translateY(0)" : "translateY(8px)",
                        transition: "opacity 220ms ease, transform 220ms ease, background 160ms ease, color 160ms ease",
                      transitionDelay: hideCopilotMenu ? "100ms" : "200ms",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = "#f88109";
                        e.currentTarget.style.color = "#ffffff";
                        e.currentTarget.style.transform = "translateY(0) scale(1.03)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = "rgba(248,129,9,0.08)";
                        e.currentTarget.style.color = "#262626";
                        e.currentTarget.style.transform = fabOpen ? "translateY(0)" : "translateY(8px)";
                      }}
                    >
                      Report Insights
                    </button>
                  )}
                  {canAssistant && (
                  <button
                    type="button"
                    onClick={() => {
                      setFabOpen(false);
                      window.dispatchEvent(
                        new CustomEvent("bic:open-ai-copilot", {
                          detail: { tab: "chat" },
                        })
                      );
                    }}
                    style={{
                      height: 40,
                      padding: "0 12px",
                      borderRadius: 999,
                      border: "1px solid #e0e5ea",
                      background: "rgba(248,129,9,0.08)",
                      boxShadow: "0 6px 16px rgba(0,0,0,0.12)",
                      cursor: "pointer",
                      fontSize: 12,
                      fontWeight: 600,
                      color: "#262626",
                      opacity: fabOpen ? 1 : 0,
                      transform: fabOpen ? "translateY(0)" : "translateY(8px)",
                      transition: "opacity 220ms ease, transform 220ms ease, background 160ms ease, color 160ms ease",
                      transitionDelay: hideCopilotMenu
                        ? isReportRoute
                          ? "200ms"
                          : "100ms"
                        : "300ms",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "#f88109";
                      e.currentTarget.style.color = "#ffffff";
                      e.currentTarget.style.transform = "translateY(0) scale(1.03)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "rgba(248,129,9,0.08)";
                      e.currentTarget.style.color = "#262626";
                      e.currentTarget.style.transform = fabOpen ? "translateY(0)" : "translateY(8px)";
                    }}
                  >
                    Insights AI Assistant
                  </button>
                  )}
              </div>
              <button
                type="button"
                title="Insights AI"
                aria-label="Insights AI"
                onPointerDown={handleFabPointerDown}
                onClick={handleFabClick}
                onMouseEnter={() => setShowDockButton(true)}
                onMouseLeave={() => setShowDockButton(false)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    handleFabClick();
                  }
                }}
                ref={fabButtonRef}
                style={{
                  width: FAB_HITBOX,
                  height: FAB_HITBOX,
                  border: "none",
                  background: "transparent",
                  padding: 0,
                  cursor: fabDragging ? "grabbing" : "grab",
                  overflow: "visible",
                  position: "relative",
                }}
              >
                <button
                  type="button"
                  aria-label="Dock Insights AI"
                  title="Dock"
                  onPointerDown={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                  }}
                  onClick={handleDockFab}
                  style={{
                    position: "absolute",
                    top: 2,
                    right: -6,
                    width: 18,
                    height: 18,
                    borderRadius: "50%",
                    border: "1px solid #e0e5ea",
                    background: "#ffffff",
                    color: "#8c8c8c",
                    fontSize: 12,
                    lineHeight: "16px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    boxShadow: "0 2px 6px rgba(0,0,0,0.12)",
                    opacity: showDockButton ? 1 : 0,
                    pointerEvents: showDockButton ? "auto" : "none",
                    transition: "opacity 120ms ease",
                  }}
                >
                  ×
                </button>
                <Lottie
                  animationData={insightsAIAnimation}
                  loop={true}
                  style={{
                    width: FAB_VISUAL,
                    height: FAB_VISUAL,
                    position: "absolute",
                    left: "50%",
                    top: "50%",
                    transform: `translate(-50%, -50%) scale(${FAB_SCALE})`,
                    pointerEvents: "none",
                  }}
                />
              </button>
            </div>
            <AICopilotPanel />
          </App>
        </div>
      </AuthenticatedTemplate>

      <UnauthenticatedTemplate>
        <div className="flex flex-col min-h-screen gap-0 mx-20">
          <div className="flex flex-row w-full h-full gap-2 flex-grow ">
            {props.children}
          </div>
        </div>
      </UnauthenticatedTemplate>
    </>
  );
};

export default PageLayout;
